import {
  Badge,
  Box,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Book, ChartBar, School, TrendingUp } from "tabler-icons-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler,
);

dayjs.extend(isBetween);
dayjs.locale("fr");

const MetricCard = ({ title, value, subtitle, icon, color }) => (
  <Card padding="md" radius="md" withBorder>
    <Group position="apart" mb="xs">
      <ThemeIcon color={color} variant="light" size="lg">
        {icon}
      </ThemeIcon>
    </Group>
    <Text size="xs" color="dimmed" transform="uppercase" weight={600} mb={4}>
      {title}
    </Text>
    <Text size="xl" weight={700} mb={4}>
      {value}
    </Text>
    {subtitle && (
      <Text size="xs" color="dimmed">
        {subtitle}
      </Text>
    )}
  </Card>
);

const StudentActivityChart = ({
  activityData,
  isLoading,
  dateRange,
  periodFilter,
}) => {
  const chartData = useMemo(() => {
    if (!activityData?.data) {
      return {
        labels: [],
        chartValues: [],
        totalDiagnostics: 0,
        activeStudents: 0,
        avgDiagPerStudent: "0.0",
        topStudent: null,
      };
    }

    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    // Générer les labels de la période
    const labels = [];
    const dataMap = new Map();

    if (isYearOrAll) {
      let currMonth = start.clone().startOf("month");
      while (currMonth.isBefore(end) || currMonth.isSame(end, "month")) {
        const label = currMonth.format("MMM YYYY");
        labels.push(label);
        dataMap.set(label, 0);
        currMonth = currMonth.add(1, "month");
      }
    } else {
      let current = start.clone();
      while (current.isBefore(end) || current.isSame(end, "day")) {
        const label = current.format("DD MMM");
        labels.push(label);
        dataMap.set(label, 0);
        current = current.add(1, "day");
      }
    }

    const items = Array.isArray(activityData.data) ? activityData.data : [];
    const isActivityList = items.some(
      (item) =>
        item?.student ||
        item?.date_diagnostic ||
        item?.activity_type ||
        item?.type_diagnostic ||
        item?.matiere,
    );

    let totalDiagnostics = 0;
    let activeStudents = 0;
    let topStudent = null;

    if (isActivityList) {
      const studentCounts = new Map();

      items.forEach((activity, index) => {
        const dateStr =
          activity?.date_diagnostic || activity?.created_at || activity?.date;
        if (!dateStr) return;

        const date = dayjs(dateStr);
        if (!date.isValid()) return;

        if (!date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")) {
          return;
        }

        const label = isYearOrAll
          ? date.format("MMM YYYY")
          : date.format("DD MMM");

        if (dataMap.has(label)) {
          dataMap.set(label, dataMap.get(label) + 1);
        } else {
          dataMap.set(label, 1);
          labels.push(label);
        }

        totalDiagnostics += 1;

        const student = activity?.student || activity?.eleve || {};
        const studentId =
          student?.id ||
          activity?.student_id ||
          activity?.eleve_id ||
          activity?.studentId ||
          activity?.eleveId ||
          null;
        const displayName =
          student?.firstName || student?.lastName
            ? `${student?.firstName || ""} ${student?.lastName || ""}`.trim()
            : activity?.student_name || activity?.eleve_name || "Eleve";
        const key = studentId
          ? `id:${studentId}`
          : displayName
            ? `name:${displayName}`
            : `unknown:${index}`;

        const existing = studentCounts.get(key) || {
          count: 0,
          firstName: student?.firstName || displayName,
          lastName: student?.lastName || "",
          displayName,
        };
        existing.count += 1;
        studentCounts.set(key, existing);
      });

      activeStudents = studentCounts.size;
      studentCounts.forEach((value) => {
        if (!topStudent || value.count > topStudent.diagnostic_count) {
          topStudent = {
            firstName: value.firstName,
            lastName: value.lastName,
            diagnostic_count: value.count,
          };
        }
      });
    } else {
      activeStudents = items.length;
      items.forEach((student) => {
        totalDiagnostics += student.diagnostic_count || 0;

        if (
          !topStudent ||
          (student.diagnostic_count || 0) > topStudent.diagnostic_count
        ) {
          topStudent = student;
        }

        // On compte 1 élève par date d'activité (last_diagnostic ou first_diagnostic)
        const dateStr = student.last_diagnostic || student.first_diagnostic;
        if (!dateStr) return;

        const date = dayjs(dateStr);
        if (!date.isValid()) return;

        const label = isYearOrAll
          ? date.format("MMM YYYY")
          : date.format("DD MMM");

        if (dataMap.has(label)) {
          dataMap.set(label, dataMap.get(label) + 1);
        } else {
          // Hors de la plage générée — ajouter le label dynamiquement
          dataMap.set(label, 1);
          labels.push(label);
        }
      });
    }

    const avgDiagPerStudent =
      activeStudents > 0
        ? (totalDiagnostics / activeStudents).toFixed(1)
        : "0.0";

    return {
      labels,
      chartValues: labels.map((l) => dataMap.get(l)),
      totalDiagnostics,
      activeStudents,
      avgDiagPerStudent,
      topStudent,
    };
  }, [activityData, dateRange, periodFilter]);

  const barChartConfig = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Élèves actifs",
        data: chartData.chartValues,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(20, 184, 166, 0.85)");
          gradient.addColorStop(1, "rgba(20, 184, 166, 0.2)");
          return gradient;
        },
        borderColor: "#14b8a6",
        borderWidth: 1.5,
        borderRadius: 4,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 13 },
        displayColors: false,
        callbacks: {
          label: (context) => `${context.parsed.y} élève(s) actif(s)`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { maxTicksLimit: 10, color: "#9ca3af" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f3f4f6", borderDash: [5, 5], drawBorder: false },
        ticks: { stepSize: 1, color: "#9ca3af" },
      },
    },
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  return (
    <Stack spacing="md">
      {/* Métriques */}
      <SimpleGrid
        cols={4}
        spacing="md"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
      >
        <MetricCard
          title="Élèves actifs"
          value={chartData.activeStudents}
          subtitle="Ayant réalisé au moins 1 activité"
          icon={<School size={20} />}
          color="teal"
        />
        <MetricCard
          title="Total activités"
          value={chartData.totalDiagnostics}
          subtitle="Sur la période"
          icon={<Book size={20} />}
          color="blue"
        />
        <MetricCard
          title="Moy. / élève"
          value={chartData.avgDiagPerStudent}
          subtitle="Activités par élève actif"
          icon={<TrendingUp size={20} />}
          color="grape"
        />
        <MetricCard
          title="Top élève"
          value={
            chartData.topStudent
              ? `${chartData.topStudent.diagnostic_count} activités`
              : "—"
          }
          subtitle={
            chartData.topStudent
              ? `${chartData.topStudent.firstName} ${chartData.topStudent.lastName}`
              : "Aucune donnée"
          }
          icon={<ChartBar size={20} />}
          color="orange"
        />
      </SimpleGrid>

      {/* Graphique */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group position="apart" mb="md">
          <Text size="lg" weight={700}>
            Activité quotidienne des élèves
          </Text>
          <Badge variant="light" color="teal" size="lg">
            {chartData.activeStudents} élèves actifs
          </Badge>
        </Group>

        <div style={{ height: "300px" }}>
          {isLoading ? (
            <Box
              h="100%"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loader size="lg" />
            </Box>
          ) : chartData.activeStudents === 0 ? (
            <Box
              h="100%"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text color="dimmed" size="sm">
                Aucune activité d'élève sur cette période
              </Text>
            </Box>
          ) : (
            <Bar options={barChartOptions} data={barChartConfig} />
          )}
        </div>

        <Text
          align="center"
          color="dimmed"
          size="xs"
          mt="md"
          transform="uppercase"
          weight={600}
          letterSpacing={0.5}
        >
          Nombre d'élèves actifs par jour
        </Text>
      </Card>
    </Stack>
  );
};

export default StudentActivityChart;
